import type { AiConnectionResponse } from "@api-client";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { Select } from "@sylviq/ui-react-web/components/ui/select";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { createAiConnection, listAiConnections, revokeAiConnection } from "@/lib/api";

type Props = {
  source: "hosted" | "byo";
  onSourceChange: (source: "hosted" | "byo") => void;
  selected: AiConnectionResponse | null;
  onSelect: (connection: AiConnectionResponse | null) => void;
};

/** Product workflow only; all controls belong to the canonical shared UI. */
export function PersonalAiConnections({ source, onSourceChange, selected, onSelect }: Props) {
  const [items, setItems] = useState<AiConnectionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [provider, setProvider] = useState<"openai" | "anthropic">("openai");
  const [model, setModel] = useState("");
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [revoke, setRevoke] = useState<AiConnectionResponse | null>(null);
  const [managing, setManaging] = useState(false);
  const alive = useRef(false);
  const operation = useRef(false);
  const sequence = useRef(0);
  const selectRef = useRef(onSelect);
  selectRef.current = onSelect;

  async function refresh() {
    const current = ++sequence.current;
    // Explicitly invalidate selection while refreshing; never guess another.
    selectRef.current(null);
    setLoading(true); setError(null);
    try {
      const result = await listAiConnections();
      if (alive.current && sequence.current === current) setItems(result.items);
    } catch {
      if (alive.current && sequence.current === current) {
        setItems([]); setError("Personal model connections could not be loaded. No prompt was sent.");
      }
    } finally {
      if (alive.current && sequence.current === current) setLoading(false);
    }
  }
  useEffect(() => {
    alive.current = true;
    void refresh();
    return () => { alive.current = false; sequence.current++; };
  }, []);

  async function save(event: FormEvent) {
    event.preventDefault();
    if (operation.current || !label.trim() || !model.trim() || !key) return;
    operation.current = true; setBusy(true); setError(null); setNotice(null);
    const input = { label: label.trim(), provider, model: model.trim(), api_key: key };
    // Write-only input is cleared before the request, on success or failure.
    setKey("");
    try {
      await createAiConnection(input);
      if (!alive.current) return;
      setNotice("Connection saved. Key and model are not verified. Select it explicitly to use BYO.");
      await refresh();
    } catch {
      if (alive.current) setError("Could not save the connection. The key was cleared; check your session and settings before trying again.");
    } finally {
      operation.current = false;
      if (alive.current) setBusy(false);
    }
  }

  async function confirmRevoke() {
    if (!revoke || operation.current) return;
    operation.current = true; setBusy(true); setError(null); setNotice(null);
    // Discard any proposal immediately, even if revocation fails remotely.
    selectRef.current(null);
    try {
      await revokeAiConnection(revoke.id);
      if (!alive.current) return;
      setRevoke(null); setNotice("Connection revoked. An upstream request already in flight cannot be recalled.");
      await refresh();
    } catch {
      if (alive.current) setError("Could not revoke the connection. It may still be active; refresh before using it again.");
    } finally {
      operation.current = false;
      if (alive.current) setBusy(false);
    }
  }

  return <section aria-label="Personal model connections" className="grid gap-3">
    <Select aria-label="AI model source" value={source} options={[{value:"hosted",label:"SYLVIQ hosted"},{value:"byo",label:"Your model (BYO)"}]}
      onChange={value => { if (value === "hosted" || value === "byo") onSourceChange(value); }} />
    {source === "byo" ? <>
      <Select aria-label="Personal model connection" value={selected?.id ?? ""} disabled={loading || busy}
        options={items.filter(item => !item.revoked_at).map(item => ({value:item.id,label:`${item.label} · ${item.provider} · ${item.model}`}))}
        placeholder="Choose your personal connection"
        onChange={id => onSelect(items.find(item => item.id === id && !item.revoked_at) ?? null)} />
      {selected ? <p className="text-sm">Provider: {selected.provider} · {selected.endpoint_origin} · Model: {selected.model}. Key/model unverified; your provider may charge your account.</p> : null}
    </> : null}
    <div className="flex flex-wrap gap-2"><Button variant="outline" disabled={loading || busy} onClick={() => void refresh()}>Refresh personal models</Button></div>
    {loading ? <p role="status">Loading personal model connections…</p> : null}
    {error ? <p role="alert">{error}</p> : null}
    {notice ? <p role="status">{notice}</p> : null}
    <Button variant="outline" onClick={() => setManaging(true)}>Manage personal model connections</Button>
    <Dialog open={managing} title="Personal model connections" description="Personal, write-only credentials; saving does not grant inference access."
      onOpenChange={open => { if (!busy) { setManaging(open); if (!open) setKey(""); } }}>
      <div className="grid gap-3 pt-3">
        <p className="text-sm">Connections belong to your account, not this organization. Saving a key does not grant AI access or verify a model. No custom endpoint or automatic fallback is used.</p>
        <form className="grid gap-3" onSubmit={event => void save(event)}>
          <label className="grid gap-1 text-sm">Connection label<Input aria-label="Connection label" value={label} maxLength={120} disabled={busy} onChange={event => setLabel(event.target.value)} /></label>
          <Select aria-label="Model provider" value={provider} disabled={busy} options={[{value:"openai",label:"OpenAI"},{value:"anthropic",label:"Anthropic"}]}
            onChange={value => { if (value === "openai" || value === "anthropic") setProvider(value); }} />
          <label className="grid gap-1 text-sm">Provider model identifier<Input aria-label="Provider model identifier" value={model} maxLength={128} disabled={busy} onChange={event => setModel(event.target.value)} /></label>
          <label className="grid gap-1 text-sm">Provider API key<Input aria-label="Provider API key" type="password" autoComplete="off" spellCheck={false} value={key} maxLength={8192} disabled={busy} onChange={event => setKey(event.target.value)} /></label>
          <p className="text-xs">Use a provider API key, not a chat subscription. The key is write-only and cleared on submit or closing this dialog.</p>
          <Button aria-label="Save personal model connection" type="submit" disabled={busy || !label.trim() || !model.trim() || !key}>Save connection</Button>
        </form>
        {items.map(item => <div key={item.id} className="flex flex-wrap items-center justify-between gap-2">
          <p>{item.label} · {item.provider} · {item.model} · {item.revoked_at ? "Revoked" : "Unverified"}</p>
          {!item.revoked_at ? <Button aria-label={`Revoke personal model ${item.label}`} variant="destructive" disabled={busy || loading} onClick={() => setRevoke(item)}>Revoke</Button> : null}
        </div>)}
        {!loading && !items.length ? <p>No personal model connections saved.</p> : null}
      </div>
    </Dialog>
    <Dialog open={revoke !== null} title="Revoke personal model connection?" description="Deletes the stored credential. This cannot recall provider requests already in flight."
      onOpenChange={open => { if (!open && !busy) setRevoke(null); }}>
      <p>{revoke?.label}</p>
      <Button disabled={busy} variant="outline" onClick={() => setRevoke(null)}>Cancel</Button>
      <Button aria-label="Confirm personal model revocation" disabled={busy} variant="destructive" onClick={() => void confirmRevoke()}>Confirm revocation</Button>
    </Dialog>
  </section>;
}
